import React from 'react';

import { TokenIcon } from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import {
    formatCardPercent,
    formatCardPnl,
    formatCardPrice,
    formatCardSize,
    formatUsdCompact,
    type PnlCardData,
} from '../utils/pnlCardData';

import './pnlCard.css';

/**
 * The shareable PnL card: real DOM at a fixed 1200×750, rasterised as-is by
 * html-to-image. Nothing here may depend on the viewport — the node is cloned
 * out of the page to be captured, so container queries, `vw` units and
 * inherited app styles would all be lost.
 */

const CARD_W = 1200;
const CARD_H = 750;
/** Printed on the card so a screenshot still says where it came from. */
const SHARE_HOME = 'snappy.trade';
/** Frame inset (see .pnl-card__frame) — the decoration layers use its box. */
const FRAME = 28;
const INNER_W = CARD_W - FRAME * 2;
const INNER_H = CARD_H - FRAME * 2;

// ── Honeycomb mesh ──────────────────────────────────────────────────────
// Pointy-top hexagons, circumradius R: width √3·R, height 2R, rows offset by
// half a width every 1.5R. The tile draws the wrapped copies too, so the
// pattern repeats without seams.
const HEX_R = 26;
const HEX_W = Math.sqrt(3) * HEX_R;
const HEX_TILE_H = HEX_R * 3;

function hexPath(cx: number, cy: number, r: number): string {
    const w = (Math.sqrt(3) * r) / 2;
    return [
        `M${cx} ${cy - r}`,
        `L${cx + w} ${cy - r / 2}`,
        `L${cx + w} ${cy + r / 2}`,
        `L${cx} ${cy + r}`,
        `L${cx - w} ${cy + r / 2}`,
        `L${cx - w} ${cy - r / 2}`,
        'Z',
    ].join(' ');
}

const HEX_TILE = [
    hexPath(0, 0, HEX_R),
    hexPath(HEX_W, 0, HEX_R),
    hexPath(HEX_W / 2, HEX_TILE_H / 2, HEX_R),
    hexPath(0, HEX_TILE_H, HEX_R),
    hexPath(HEX_W, HEX_TILE_H, HEX_R),
].join(' ');

/** Sparks scattered in the upper field — fixed, so the card is deterministic. */
const SPARKS: Array<{ x: number; y: number; o: number }> = [
    { x: 0.36, y: 0.22, o: 0.5 },
    { x: 0.52, y: 0.42, o: 0.32 },
    { x: 0.63, y: 0.15, o: 0.45 },
    { x: 0.78, y: 0.5, o: 0.3 },
    { x: 0.86, y: 0.26, o: 0.42 },
    { x: 0.93, y: 0.61, o: 0.28 },
    { x: 0.45, y: 0.63, o: 0.24 },
];

/** The trend line the snail rides: flat, a step up, then out to the dot. */
const LINE_Y = 0.63 * INNER_H;
const LINE_RISE = LINE_Y - 44;
const LINE_PATH = [
    `M0 ${LINE_Y}`,
    `L${INNER_W * 0.52} ${LINE_Y}`,
    `C${INNER_W * 0.58} ${LINE_Y} ${INNER_W * 0.575} ${LINE_RISE} ${INNER_W * 0.635} ${LINE_RISE}`,
    `L${INNER_W * 0.945} ${LINE_RISE}`,
].join(' ');

const TargetIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <circle
            cx="12"
            cy="12"
            r="3.2"
            stroke="currentColor"
            strokeWidth="1.8"
        />
        <path
            d="M12 1.6v3.2M12 19.2v3.2M22.4 12h-3.2M4.8 12H1.6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);

interface StatProps {
    label: string;
    value: string;
    icon?: React.ReactNode;
    muted?: boolean;
}

const Stat: React.FC<StatProps> = ({ label, value, icon, muted }) => (
    <div className={cn('pnl-card__stat', muted && 'pnl-card__stat--muted')}>
        {icon && <span className="pnl-card__stat-icon">{icon}</span>}
        <span>
            <span className="pnl-card__stat-label">{label}</span>
            <span className="pnl-card__stat-value">{value}</span>
        </span>
    </div>
);

export interface PnlShareCardProps {
    data: PnlCardData;
    /** Redacts the dollar figures so the percentage can be shared alone. */
    hideBalances?: boolean;
}

/** Rendered into the dialog preview AND captured — they are the same node. */
export const PnlShareCard = React.forwardRef<HTMLDivElement, PnlShareCardProps>(
    ({ data, hideBalances = false }, ref) => {
        const gain = data.pnlValue >= 0;
        const redact = (text: string) => (hideBalances ? '•••••' : text);

        return (
            <div
                ref={ref}
                className={cn('pnl-card', !gain && 'pnl-card--loss')}
                style={{ width: CARD_W, height: CARD_H }}
            >
                <div className="pnl-card__frame">
                    <svg
                        className="pnl-card__hex"
                        viewBox={`0 0 ${INNER_W} ${INNER_H}`}
                        aria-hidden="true"
                    >
                        <defs>
                            <pattern
                                id="pnl-hex"
                                width={HEX_W}
                                height={HEX_TILE_H}
                                patternUnits="userSpaceOnUse"
                            >
                                <path
                                    d={HEX_TILE}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                />
                            </pattern>
                        </defs>
                        <rect
                            width={INNER_W}
                            height={INNER_H}
                            fill="url(#pnl-hex)"
                        />
                    </svg>

                    {SPARKS.map((spark, i) => (
                        <span
                            key={i}
                            className="pnl-card__spark"
                            style={{
                                left: spark.x * INNER_W,
                                top: spark.y * INNER_H,
                                opacity: spark.o,
                            }}
                        />
                    ))}

                    <svg
                        className="pnl-card__line"
                        viewBox={`0 0 ${INNER_W} ${INNER_H}`}
                        aria-hidden="true"
                    >
                        <path
                            d={LINE_PATH}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            style={{ color: 'var(--accent)' }}
                        />
                        <circle
                            cx={INNER_W * 0.945}
                            cy={LINE_RISE}
                            r="9"
                            fill="#ffffff"
                        />
                    </svg>

                    <svg
                        width="0"
                        height="0"
                        aria-hidden="true"
                        style={{ position: 'absolute' }}
                    >
                        <filter
                            id="pnl-snail-loss"
                            colorInterpolationFilters="sRGB"
                        >
                            <feColorMatrix
                                type="matrix"
                                values="0.40 0.72 0 0 0  0.21 0 0 0 0  0 0.43 0 0 0  0 0 0 1 0"
                            />
                        </filter>
                    </svg>

                    {/* The brand snail, motion lines and all. Same-origin, so
                        html-to-image inlines it into the capture. */}
                    <img
                        className="pnl-card__snail"
                        src="/snappy-logo.png"
                        alt=""
                        width={264}
                    />

                    <div className="pnl-card__content">
                        <div className="pnl-card__top">
                            <span className="pnl-card__wordmark">
                                Snappy<em>Trade</em>
                            </span>
                            <span className="pnl-card__tagline">
                                <b>In. Out. Done.</b>
                            </span>
                        </div>

                        <div className="pnl-card__top pnl-card__top--coin">
                            <span className="pnl-card__coin">
                                <span className="pnl-card__coin-icon">
                                    <TokenIcon token={data.coin} />
                                </span>
                                {data.coin}
                            </span>
                            <span className="pnl-card__pill pnl-card__pill--side">
                                <TargetIcon size={28} />
                                {data.side}
                                {data.leverage !== null && ` ${data.leverage}X`}
                            </span>
                        </div>

                        {/* Hiding balances promotes the percentage to the
                            headline rather than redacting it. Showing the
                            dollar PnL beside a hidden stake would not be
                            private anyway — stake = pnl ÷ pnl%. */}
                        <div className="pnl-card__hero">
                            {hideBalances
                                ? formatCardPercent(data.pnlPercent)
                                : formatCardPnl(data.pnlValue)}
                        </div>

                        {!hideBalances && (
                            <div className="pnl-card__roi">
                                <span>{gain ? '▲' : '▼'}</span>
                                {formatCardPercent(data.pnlPercent)}
                            </div>
                        )}

                        <div className="pnl-card__stats">
                            <Stat
                                label="Invested"
                                value={redact(formatUsdCompact(data.invested))}
                            />
                            <Stat
                                label="Position"
                                value={redact(
                                    formatUsdCompact(data.positionValue),
                                )}
                            />
                            <Stat
                                label="Size"
                                value={redact(
                                    formatCardSize(data.sizeToken, data.coin),
                                )}
                            />
                            <Stat
                                muted
                                label="Entry price"
                                value={formatCardPrice(data.entryPrice)}
                            />
                            <Stat
                                muted
                                label={
                                    data.kind === 'unrealized'
                                        ? 'Mark price'
                                        : 'Exit price'
                                }
                                value={formatCardPrice(data.markPrice)}
                            />
                            <span className="pnl-card__home">{SHARE_HOME}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

PnlShareCard.displayName = 'PnlShareCard';

export { CARD_H, CARD_W };
